import React, { useState } from 'react';
import { 
  Cpu, 
  Users, 
  Database, 
  ShieldCheck, 
  Zap, 
  ArrowRight, 
  CloudLightning,
  Lock,
  FileCheck,
  BarChart4,
  Workflow,
  Globe,
  ListChecks
} from 'lucide-react';
import Header from './Header';

export default function BlueGeckoFalcon360HR() {
  const [activeSection, setActiveSection] = useState('overview');
  const [activeFeature, setActiveFeature] = useState(null);
  const [activeBenefit, setActiveBenefit] = useState(null);

  // Generate unique particle IDs on component mount
  const particles = React.useMemo(() => 
    Array.from({ length: 100 }).map((_, i) => ({
      id: `particle-${Date.now()}-${i}`,
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 5 + 2,
      opacity: Math.random() * 0.5,
      duration: Math.random() * 20 + 10,
      delay: Math.random() * 10
    })),
    []
  );

  // Key features of Falcon360 for HR & Staffing
  const keyFeatures = [
    {
      icon: <Database className="text-blue-400" size={32} />,
      title: "Automated Field Mapping",
      description: "Smart mapping for employee master data, payroll, org structures, contracts, and job postings"
    },
    {
      icon: <CloudLightning className="text-purple-400" size={32} />,
      title: "GenAI-Powered Validation",
      description: "Intelligent rules to detect gaps in critical fields (e.g., missing IDs, start dates, roles)"
    },
    {
      icon: <Workflow className="text-green-400" size={32} />,
      title: "Role-Based Filtering",
      description: "Advanced segmentation of global workforce data during migration with smart categorization"
    },
    {
      icon: <FileCheck className="text-teal-400" size={32} />,
      title: "Audit Trails & Dashboards",
      description: "Comprehensive compliance tracking and one-click reconciliation dashboards for HR stakeholders"
    }
  ];

  // Business benefits
  const keyBenefits = [
    {
      icon: <Zap className="text-yellow-400" size={32} />,
      title: "Accelerated Cloud Adoption",
      description: "Streamline transitions to modern platforms like Workday, SuccessFactors, and Bullhorn"
    },
    {
      icon: <ShieldCheck className="text-red-400" size={32} />,
      title: "HR Data Integrity",
      description: "Ensure flawless data accuracy for payroll, benefits, and workforce management"
    },
    {
      icon: <Lock className="text-indigo-400" size={32} />,
      title: "Reduced Risk",
      description: "Minimize employee data issues during go-live with intelligent error prevention"
    },
    {
      icon: <Globe className="text-emerald-400" size={32} />,
      title: "Global HR Continuity",
      description: "Support seamless transition for global HR operations across multiple regions"
    }
  ];

  const complianceStandards = [
    { id: 'gdpr', name: "GDPR", color: "bg-blue-500" },
    { id: 'hipaa', name: "HIPAA", color: "bg-purple-500" },
    { id: 'iso27001', name: "ISO 27001", color: "bg-green-500" },
    { id: 'ccpa', name: "CCPA", color: "bg-yellow-500" },
    { id: 'soc2', name: "SOC 2", color: "bg-red-500" }
  ];

  return (
        <div className="relative bg-gradient-to-b from-gray-900 via-gray-900 to-indigo-900 min-h-screen text-white overflow-hidden">
      <Header />
    <div
      className="relative bg-gradient-to-b from-transparent via-indigo-900 to-indigo-900 min-h-screen text-white overflow-hidden"
      style={{ backgroundColor: 'rgba(2, 6, 23, 0.9)' }}
    >
      {/* Animated Background Particles */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full bg-indigo-500"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              opacity: particle.opacity,
              animation: `float ${particle.duration}s linear infinite`,
              animationDelay: `${particle.delay}s`
            }}
          />
        ))}
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12 relative">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="bg-indigo-600 bg-opacity-30 p-3 rounded-full">
              <Users size={40} className="text-indigo-300 animate-pulse" />
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-400 to-purple-300 bg-clip-text text-transparent">
                BlueGecko Falcon Mapping
              </h1>
              <div className="flex items-center justify-center gap-2 mt-1">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <p className="text-indigo-200">Intelligent Data Migration for HR & Staffing Systems</p>
              </div>
            </div>
          </div>
          <div className="bg-indigo-900 bg-opacity-30 backdrop-blur-sm w-fit mx-auto px-6 py-2 rounded-full border border-indigo-500 border-opacity-30 mb-8">
            <span className="text-indigo-200">Powered by Advanced GenAI Technology</span>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex justify-center mb-12">
          {[
            { key: 'overview', label: 'Why HR & Staffing Needs It' },
            { key: 'features', label: 'How It Works' },
            { key: 'benefits', label: 'Business Value' }
          ].map((tab) => (
            <button
              key={tab.key}
              className={`
                px-6 py-3 mx-2 rounded-lg transition-all duration-300
                ${activeSection === tab.key 
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-900/50' 
                  : 'bg-gray-800 bg-opacity-50 text-indigo-200 hover:bg-gray-700 hover:bg-opacity-50'}
              `}
              onClick={() => setActiveSection(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Sections */}
        {activeSection === 'overview' && (
          <div className="bg-gray-900 bg-opacity-50 backdrop-blur-lg rounded-2xl p-8 border border-indigo-500 border-opacity-30 shadow-xl">
            <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-indigo-300 to-purple-300 bg-clip-text text-transparent flex items-center">
              <Users size={24} className="mr-2 text-indigo-400" />
              Why HR & Staffing Needs Smart Migration
            </h2>
            <div className="space-y-6">
              <div className="flex items-start gap-4 bg-gray-800 bg-opacity-50 p-5 rounded-xl border border-indigo-800 border-opacity-30 hover:border-indigo-500 hover:border-opacity-50 transition-all duration-300">
                <div className="min-w-8 mt-1 text-indigo-400"><ArrowRight size={20} /></div>
                <div>
                  <h3 className="text-indigo-200 font-medium mb-2">Legacy System Transitions</h3>
                  <p className="text-gray-300">Migration from legacy HRIS, ATS, or payroll systems (e.g., SAP HCM, Bullhorn, Oracle PeopleSoft, Workday) requires specialized expertise</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 bg-gray-800 bg-opacity-50 p-5 rounded-xl border border-indigo-800 border-opacity-30 hover:border-indigo-500 hover:border-opacity-50 transition-all duration-300">
                <div className="min-w-8 mt-1 text-indigo-400"><ArrowRight size={20} /></div>
                <div>
                  <h3 className="text-indigo-200 font-medium mb-2">Data Structure Complexity</h3>
                  <p className="text-gray-300">Complex employee, contractor, and timesheet data structures with intricate relationships and dependencies</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 bg-gray-800 bg-opacity-50 p-5 rounded-xl border border-indigo-800 border-opacity-30 hover:border-indigo-500 hover:border-opacity-50 transition-all duration-300">
                <div className="min-w-8 mt-1 text-indigo-400"><ArrowRight size={20} /></div>
                <div>
                  <h3 className="text-indigo-200 font-medium mb-2">Compliance Requirements</h3>
                  <p className="text-gray-300">Sensitive data compliance (GDPR, HIPAA, ISO) with no room for error during migration processes</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 bg-gray-800 bg-opacity-50 p-5 rounded-xl border border-indigo-800 border-opacity-30 hover:border-indigo-500 hover:border-opacity-50 transition-all duration-300">
                <div className="min-w-8 mt-1 text-indigo-400"><ArrowRight size={20} /></div>
                <div>
                  <h3 className="text-indigo-200 font-medium mb-2">Historical Data Volume</h3>
                  <p className="text-gray-300">High volume of historical records and position hierarchies that must be preserved intact</p>
                </div>
              </div>
            </div>
            
            {/* Compliance Standards */}
            <div className="mt-8">
              <h3 className="text-xl font-semibold text-indigo-300 mb-4">Compliance Standards Supported</h3>
              <div className="flex flex-wrap gap-3">
                {complianceStandards.map((standard) => (
                  <div key={standard.id} className="bg-gray-800 bg-opacity-40 px-4 py-2 rounded-full flex items-center border border-gray-700">
                    <div className={`w-3 h-3 ${standard.color} rounded-full mr-2`}></div>
                    <span className="text-gray-200">{standard.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeSection === 'features' && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-indigo-300 to-purple-300 bg-clip-text text-transparent text-center flex items-center justify-center">
              <Cpu size={24} className="mr-2 text-indigo-400" />
              How Falcon Mapping Supports HR & Staffing
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {keyFeatures.map((feature) => {
                const featureKey = `feature-${feature.title.toLowerCase().replaceAll(' ', '-')}`;
                return (
                  <button 
                    key={featureKey}
                    className={`
                      p-6 rounded-xl transition-all duration-300 cursor-pointer text-left border-0 bg-none
                      ${activeFeature === featureKey 
                        ? 'bg-gray-800 border-2 border-indigo-500 shadow-2xl' 
                        : 'bg-gray-900 bg-opacity-50 border border-indigo-800 border-opacity-30'}
                    `}
                    onMouseEnter={() => setActiveFeature(featureKey)}
                    onMouseLeave={() => setActiveFeature(null)}
                    onClick={() => setActiveFeature(featureKey)}
                  >
                    <div className="flex items-center mb-4">
                      <div className="bg-indigo-900 bg-opacity-30 p-3 rounded-full">
                        {feature.icon}
                      </div>
                      <h3 className="ml-4 text-xl font-semibold text-indigo-200">
                        {feature.title}
                      </h3>
                    </div>
                    <p className="text-gray-300">
                      {feature.description}
                    </p>
                  </button>
                );
              })}
            </div>
            
            <div className="bg-gray-900 bg-opacity-50 backdrop-blur-lg rounded-xl p-6 border border-indigo-500 border-opacity-30">
              <h3 className="text-xl font-semibold text-indigo-300 mb-4">Key HR Systems Supported</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-800 bg-opacity-50 p-4 rounded-lg border border-indigo-800 border-opacity-30 text-center">
                  <div className="flex items-center justify-center mb-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                    <span className="text-indigo-200">SAP SuccessFactors</span>
                  </div>
                </div>
                <div className="bg-gray-800 bg-opacity-50 p-4 rounded-lg border border-indigo-800 border-opacity-30 text-center">
                  <div className="flex items-center justify-center mb-2">
                    <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                    <span className="text-indigo-200">Workday</span>
                  </div>
                </div>
                <div className="bg-gray-800 bg-opacity-50 p-4 rounded-lg border border-indigo-800 border-opacity-30 text-center">
                  <div className="flex items-center justify-center mb-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-indigo-200">Oracle HCM</span>
                  </div>
                </div>
                <div className="bg-gray-800 bg-opacity-50 p-4 rounded-lg border border-indigo-800 border-opacity-30 text-center">
                  <div className="flex items-center justify-center mb-2">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                    <span className="text-indigo-200">Bullhorn</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-indigo-900 bg-opacity-30 p-6 rounded-xl border border-indigo-500 border-opacity-20">
              <div className="flex items-start gap-4">
                <div className="bg-indigo-800 bg-opacity-30 p-3 rounded-full mt-1">
                  <ListChecks size={24} className="text-indigo-300" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-indigo-200 mb-2">HR-Specific AI Capabilities</h3>
                  <p className="text-gray-300">Falcon Mapping's specialized AI understands the nuances of HR data structures, employee hierarchies, compensation frameworks, and staffing models to ensure precise migrations with contextual awareness.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'benefits' && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-indigo-300 to-purple-300 bg-clip-text text-transparent text-center flex items-center justify-center">
              <BarChart4 size={24} className="mr-2 text-indigo-400" />
              Business Value Delivered
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {keyBenefits.map((benefit) => {
                const benefitKey = `benefit-${benefit.title.toLowerCase().replaceAll(' ', '-')}`;
                return (
                  <button 
                    key={benefitKey}
                    className={`
                      p-6 rounded-xl transition-all duration-300 relative overflow-hidden cursor-pointer text-left border-0 bg-none
                      ${activeBenefit === benefitKey 
                        ? 'bg-gradient-to-br from-indigo-900 to-indigo-800 border-2 border-indigo-500 shadow-2xl' 
                        : 'bg-gray-900 bg-opacity-50 border border-indigo-800 border-opacity-30'}
                    `}
                    onMouseEnter={() => setActiveBenefit(benefitKey)}
                    onMouseLeave={() => setActiveBenefit(null)}
                    onClick={() => setActiveBenefit(benefitKey)}
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500 opacity-10 rounded-full blur-xl"></div>
                    <div className="flex items-center mb-4">
                      <div className="bg-indigo-900 bg-opacity-30 p-3 rounded-full">
                        {benefit.icon}
                      </div>
                      <h4 className="ml-4 text-xl font-semibold text-indigo-200">
                        {benefit.title}
                      </h4>
                    </div>
                    <p className="text-gray-300 relative z-10">
                      {benefit.description}
                    </p>
                  </button>
                );
              })}
            </div>
            
            <div className="bg-gradient-to-r from-indigo-900 to-purple-900 p-8 rounded-xl border border-indigo-500 border-opacity-30 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Ready to Transform Your HR Systems?</h3>
              <p className="text-indigo-200 mb-6">
                Experience the power of AI-driven migration with BlueGecko Falcon Mapping and accelerate your HR digital transformation.
              </p>
              <button className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold py-4 px-10 rounded-lg transition-all duration-300 flex items-center justify-center mx-auto group">
                Schedule a Demo
                <ArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Timeline Visualization */}
        {activeSection === 'benefits' && (
          <div className="mt-12 bg-gray-900 bg-opacity-50 backdrop-blur-lg rounded-xl p-8 border border-indigo-500 border-opacity-30">
            <h3 className="text-xl font-bold text-indigo-300 mb-6">Implementation Timeline Advantage</h3>
            <div className="relative">
              {/* Traditional Timeline */}
              <div className="mb-8">
                <h4 className="text-gray-300 mb-3">Traditional Migration</h4>
                <div className="h-8 bg-gray-800 rounded-lg relative">
                  <div className="absolute inset-y-0 left-0 bg-gray-600 w-3/4 rounded-l-lg flex items-center justify-end px-4">
                    <span className="text-xs text-white">12-18 months</span>
                  </div>
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-400">
                  <span>Planning</span>
                  <span>Implementation</span>
                  <span>Testing</span>
                  <span>Go-Live</span>
                </div>
              </div>
              
              {/* Falcon360 Timeline */}
              <div>
                <h4 className="text-indigo-300 mb-3">With Falcon Mapping</h4>
                <div className="h-8 bg-gray-800 rounded-lg relative">
                  <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-600 to-purple-600 w-2/5 rounded-l-lg flex items-center justify-end px-4">
                    <span className="text-xs text-white">4-6 months</span>
                  </div>
                </div>
                <div className="flex justify-between mt-2 text-xs text-indigo-200">
                  <span>AI-Enhanced Planning</span>
                  <span>Automated Implementation</span>
                  <span>Validation</span>
                  <span>Go-Live</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="flex flex-wrap justify-center bg-indigo-900 bg-opacity-30 px-4 py-2 rounded-full text-xs items-center gap-2 border border-indigo-500 border-opacity-20 w-fit mx-auto">
            <span className="h-2 w-2 bg-indigo-400 rounded-full animate-pulse"></span>
            <span className="text-indigo-300">Powered by BlueGecko GenAI Technology</span>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}